import { Nullable } from "../../../Types/Common";
import { IEmployyeManagerUpdate } from "../../../Types/EmployeeManagerUpdate";
import { IBaseResponse } from "../../../Types/UserProfileDetails";
import {
  ICompOffDetailResponse,
  ICompOffListParams,
  ICompOffListResponse,
  ICompOffMutationResponse,
  ICompOffPayload,
  ICompOffUpdateParams,
} from "../../../features/hrZone/CompOffManagement/types";
import ApiService from "../../ApiService";

enum HRZoneEndpoints {
  MANAGER_UPDATE = "/employee/manager/update",
  EMPLOYEE_STATUS_UPDATE = "/hr/employee-status/update",
  EMPLOYEE_EDIT_OPTIONS = "/hr/employee-profile/edit-option",
  EMPLOYEE_MASTER_DATA = "/hr/employee-master-data",
  EMPLOYEE_REIMBURSEMENT = "/report/employee-reimbursement",
  EMPLOYEE_BULK_ATTENDANCE_CORRECTION = "/hr/mark-bulk-attendance",
  EMPLOYEE_PROBATION = "/hr/employee-under-probation",
  EMPLOYEE_UPDATE_PROBARTION = "/hr/update-probation",
  COMP_OFF = "/hr/compoff",
}

export interface IUserMasterDetails {
  // [key: string]: string | undefined; // ← Add this line

  aadhar_number: Nullable<string>;
  account_number: Nullable<string>;
  account_type: Nullable<string>;
  anniversary: Nullable<string>;
  bank_name: Nullable<string>;
  centre_name: Nullable<string>;
  department_name: Nullable<string>;
  designation: Nullable<string>;
  dob: Nullable<string>;
  doj: Nullable<string>;
  employee_code: Nullable<string>;
  employee_name: Nullable<string>;
  father_name: Nullable<string>;
  mother_name: Nullable<string>;
  gender: Nullable<string>;
  id: Nullable<number>;
  ifsc_code: Nullable<string>;
  job_type: Nullable<string>;
  marital_status: Nullable<string>;
  official_email: Nullable<string>;
  pan_number: Nullable<string>;
  phone_number: Nullable<string>;
  spouse_name: Nullable<string>;
  status: Nullable<string>;
  exit_date: Nullable<string>;
}

export interface IUserMasterDetailsPagination {
  current_page: number;
  data: IUserMasterDetails[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: Nullable<string>;
  next_page_url: Nullable<string>;
  path: string;
  per_page: string;
  prev_page_url: Nullable<string>;
  total: Nullable<number>;
  to: Nullable<number>;
}

export interface IUserMasterDetailsRepsonse {
  count: number;
  success: boolean;
  data: IUserMasterDetailsPagination | IUserMasterDetails[];
}

export interface IMarkBulkEmployeeAttendanceCorrectionParams {
  user_id: number;
  dates: Array<string>;
  in_time: string;
  out_time: string;
}

export interface IEmployeeUnderProbationList {
  user_id: Nullable<number>;
  doj: Nullable<string>;
  email: Nullable<string>;
  department_name: Nullable<string>;
  emp_code: Nullable<string>;
  name: Nullable<string>;
  probation_date: Nullable<string>;
  probation_remark: Nullable<string>;
  probation_status: Nullable<string>;
  extended_date: Nullable<string>;
  form_id: Nullable<number>;
}

export interface IEmployeeUnderProbationListResponse {
  status: boolean;
  message: string;
  data: IEmployeeUnderProbationList[];
}

export interface IEmployeeUpdateProbationParams {
  user_id: number;
  probation_status: string;
  probation_remark: string;
  extended_date: string | null;
}

export interface IEmployeeMasterReportParams {
  page?: number;
  size?: number;
  name?: string;
  status?: number;
  doj?: boolean;
  dojStartDate?: string;
  dojEndDate?: string;
  dob?: boolean;
  dobStartDate?: string;
  dobEndDate?: string;
  jobType?: string;
  departmentId?: number;
  centerId?: number;
  anniversary?: boolean;
  anniversaryStartDate?: string;
  anniversaryEndDate?: string;
  exit?: boolean;
  exitStartDate?: string;
  exitEndDate?: string;
}

class HRService extends ApiService {
  async employeeStatusUpdate(employeeStatusParams: {
    user_id: number;
    status: string;
  }) {
    return this.post<IBaseResponse>(
      HRZoneEndpoints.EMPLOYEE_STATUS_UPDATE,
      employeeStatusParams
    );
  }

  async updateEmployeeManger(employeeMangerData: IEmployyeManagerUpdate) {
    return this.post<IBaseResponse>(
      HRZoneEndpoints.MANAGER_UPDATE,
      employeeMangerData
    );
  }

  async employeeEditOptions(employeeData: {
    user_id: number;
    edit_option: string;
  }) {
    return this.post<IBaseResponse>(
      HRZoneEndpoints.EMPLOYEE_EDIT_OPTIONS,
      employeeData
    );
  }

  async employeeMasterData({
    page,
    size,
    name,
    status,
    doj,
    dojStartDate,
    dojEndDate,
    dob,
    dobEndDate,
    dobStartDate,
    departmentId,
    jobType,
    centerId,
    anniversary,
    anniversaryStartDate,
    anniversaryEndDate,
    exit,
    exitEndDate,
    exitStartDate,
  }: IEmployeeMasterReportParams) {
    const params = new URLSearchParams();

    if (page) params.append("page", page?.toString());
    if (size) params.append("size", size?.toString());

    if (name) params.append("name", name);
    if (typeof status === "number") params.append("status", status.toString());
    if (doj && dojStartDate && dojEndDate) {
      params.append("doj", doj.toString());
      params.append("start_date", dojStartDate); // YYYY-MM-DD
      params.append("end_date", dojEndDate); // YYYY-MM-DD
    }
    if (dob && dobStartDate && dobEndDate) {
      params.append("dob", dob.toString());
      params.append("start_date", dobStartDate);
      params.append("end_date", dobEndDate);
    }
    if (anniversary && anniversaryStartDate && anniversaryEndDate) {
      params.append("anniversary", anniversary.toString());
      params.append("start_date", anniversaryStartDate);
      params.append("end_date", anniversaryEndDate);
    }
    if (exit && exitEndDate && exitStartDate) {
      params.append("exit", exit.toString());
      params.append("start_date", exitStartDate);
      params.append("end_date", exitEndDate);
    }

    if (jobType) params.append("job_type", jobType);
    if (typeof departmentId === "number")
      params.append("department", departmentId.toString());

    if (typeof centerId === "number")
      params.append("center", centerId.toString());

    return this.get<IUserMasterDetailsRepsonse>(
      `${HRZoneEndpoints.EMPLOYEE_MASTER_DATA}?${params.toString()}`
    );
  }

  async markBulkEmployeeAttendanceCorrection(
    bulkEmployyeAttendanceCorrectionParams: IMarkBulkEmployeeAttendanceCorrectionParams
  ): Promise<{ message: string; status: string }> {
    return this.post(
      HRZoneEndpoints.EMPLOYEE_BULK_ATTENDANCE_CORRECTION,
      bulkEmployyeAttendanceCorrectionParams
    );
  }

  async employeeUnderProbation(
    probationStatus: string
  ): Promise<IEmployeeUnderProbationListResponse> {
    return this.post<IEmployeeUnderProbationListResponse>(
      `${HRZoneEndpoints.EMPLOYEE_PROBATION}?probation_status_filter=${probationStatus}`
    );
  }

  async updateEmployeeProbation({
    user_id,
    probation_status,
    probation_remark,
    extended_date,
  }: IEmployeeUpdateProbationParams): Promise<IBaseResponse> {
    return this.post<IBaseResponse>(
      HRZoneEndpoints.EMPLOYEE_UPDATE_PROBARTION,
      {
        user_id,
        probation_status,
        probation_remark,
        extended_date,
      }
    );
  }

  async sendProbationReminderMail(user_id: number): Promise<IBaseResponse> {
    return this.post<IBaseResponse>(
      `/singleUser-probation-reminder/${user_id}`
    );
  }

  async compOffList({
    page,
    size,
    user_id,
    status,
    from,
    to,
  }: ICompOffListParams): Promise<ICompOffListResponse> {
    const params = new URLSearchParams();
    if (page && typeof page === "number") params.append("page", String(page));
    if (size && typeof size === "number") params.append("size", String(size));
    if (user_id && typeof user_id === "number")
      params.append("user_id", String(user_id));
    if (status) params.append("status", status);
    if (from) params.append("from", from);
    if (to) params.append("to", to);

    const queryString = params.toString();

    return this.get<ICompOffListResponse>(
      `${HRZoneEndpoints.COMP_OFF}${queryString ? `?${queryString}` : ""}`
    );
  }

  async compOffDetail(id: number): Promise<ICompOffDetailResponse> {
    return this.get<ICompOffDetailResponse>(`${HRZoneEndpoints.COMP_OFF}/${id}`);
  }

  async addCompOff(
    payload: ICompOffPayload
  ): Promise<ICompOffMutationResponse> {
    return this.post<ICompOffMutationResponse>(
      HRZoneEndpoints.COMP_OFF,
      payload
    );
  }

  async updateCompOff({
    id,
    payload,
  }: ICompOffUpdateParams): Promise<ICompOffMutationResponse> {
    return this.put<ICompOffMutationResponse>(
      `${HRZoneEndpoints.COMP_OFF}/${id}`,
      payload
    );
  }
}
export const hrService = new HRService();
